<template>
  <div class="cms-reviews">
    <cms-page-header
      :title="$t('cms.reviews.title')"
      :create-text="$t('cms.reviews.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.reviews.filters.language')">
        <select v-model="selectedLanguage" class="cms-select">
          <option v-for="language in languageOptions" :key="language.value" :value="language.value">
            {{ language.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.reviews.filters.activity')">
        <select v-model="selectedActivity" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="activity in activityOptions" :key="activity.value" :value="activity.value">
            {{ activity.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.reviews.filters.rating')">
        <select v-model="selectedRating" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="(label, value) in ratingOptions" :key="value" :value="value">
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.reviews.filters.status')">
        <select v-model="selectedStatus" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option value="pending">{{ $t('cms.reviews.statuses.pending') }}</option>
          <option value="approved">{{ $t('cms.reviews.statuses.approved') }}</option>
          <option value="rejected">{{ $t('cms.reviews.statuses.rejected') }}</option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.reviews.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadReviews">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
      </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-reviews-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadReviews">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-reviews-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="filteredReviews.length === 0"
      :message="$t('cms.reviews.list.emptyStateTitle')"
      :hint="$t('cms.reviews.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="360px">
      <cms-card
        v-for="review in filteredReviews"
        :key="review.id"
        clickable
        class="cms-review-card"
        @click="navigateToEdit(review.id)"
      >
        <template #actions>
          <cms-icon-button
            v-if="review.status === 'pending' || review.status === 'rejected'"
            icon="check"
            variant="success"
            :tooltip="$t('cms.reviews.approve')"
            @click.stop="updateStatus(review, 'approved')"
          />
          <cms-icon-button
            v-if="review.status === 'pending' || review.status === 'approved'"
            icon="close"
            variant="danger"
            :tooltip="$t('cms.reviews.reject')"
            @click.stop="updateStatus(review, 'rejected')"
          />
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEdit(review.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === review.id"
            :tooltip="$t('general.delete')"
            @click.stop="deleteReview(review)"
          />
        </template>

        <div v-if="review.featuredImage" class="cms-review-preview" :style="{ backgroundImage: `url(${review.featuredImage})` }" />

        <div class="cms-review-header">
          <h3 class="cms-review-title">{{ review.title }}</h3>
          <div class="cms-review-badges">
            <div :class="['cms-status-badge', `status-${review.status}`]">
              {{ $t(`cms.reviews.statuses.${review.status}`) }}
            </div>
            <div class="cms-review-rating">
              <v-icon icon="star" size="sm" />
              <span>{{ review.rating }}/5</span>
            </div>
          </div>
        </div>

        <div v-if="review.contentPreview" class="cms-review-content" v-html="review.contentPreview"></div>

        <ul class="cms-review-meta">
          <li v-if="review.reviewDate">
            <v-icon icon="calendar" size="sm" />
            <span>{{ review.reviewDateLabel }}</span>
          </li>
          <li v-if="review.activityName">
            <v-icon icon="map" size="sm" />
            <span>{{ review.activityName }}</span>
          </li>
        </ul>

        <template #footer>
          <div class="cms-card-footer">
            <span class="cms-review-date">
              {{ review.updatedAtLabel }}
            </span>
            <span class="cms-review-locale">
              {{ review.locale?.toUpperCase() }}
            </span>
          </div>
        </template>
      </cms-card>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsIconButton,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsReviewCard {
  id: number;
  title: string;
  content: string;
  contentPreview: string;
  rating: number;
  authorName: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewDate: string | null;
  reviewDateLabel: string;
  activityId: number | null;
  activityName: string;
  updatedAt: string | null;
  updatedAtLabel: string;
  locale: string;
  featuredImage?: string;
}

export default defineComponent({
  name: 'TheCmsReviews',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsIconButton,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { locales, resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));

    const reviews = ref<CmsReviewCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const deletingId = ref<number | null>(null);

    const selectedLanguage = ref(currentLocale.value);
    const selectedActivity = ref('');
    const selectedRating = ref('');
    const selectedStatus = ref('');
    const searchQuery = ref('');

    // Pagination state
    const currentPage = ref(1);
    const perPage = ref(25);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const activities = ref<{ value: string; label: string }[]>([]);

    const languageOptions = computed(() =>
      locales.value.map((locale) => ({
        value: locale,
        label: locale.toUpperCase(),
      })),
    );

    const ratingOptions = computed(() => ({
      1: t('cms.reviews.ratings.1'),
      2: t('cms.reviews.ratings.2'),
      3: t('cms.reviews.ratings.3'),
      4: t('cms.reviews.ratings.4'),
      5: t('cms.reviews.ratings.5'),
    }));

    const activityOptions = computed(() => activities.value);

    // Since filtering is now done server-side, filteredReviews just returns reviews
    const filteredReviews = computed(() => reviews.value);

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const formatDate = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString();
    };

    const formatDateTime = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleString();
    };

    const stripHtml = (html: string, maxLength = 150) => {
      if (!html) return '';
      const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const extractTranslations = (review: any) => {
      const relationship = extractRelationship(review, 'translations');
      const normalizedRelationship = normalizeResource(relationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(review?.translations)) {
        return review.translations;
      }
      if (normalizedRelationship) {
        return [normalizedRelationship];
      }
      return [];
    };

    const extractMediaItems = (review: any) => {
      const relationship = extractRelationship(review, 'media');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) {
        return normalized;
      }
      if (Array.isArray(review?.media)) {
        return review.media;
      }
      return [];
    };

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item?.attributes || {};
      return (
        item.original_url ||
        item.url ||
        attributes.original_url ||
        attributes.url ||
        attributes.preview_url ||
        ''
      );
    };

    const mapReviewRecord = (item: any): CmsReviewCard => {
      const normalized = normalizeResource(item) || item;
      const translations = extractTranslations(normalized);
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === selectedLanguage.value) ||
        translations[0] ||
        {};

      const activityRelation = normalizeResource(extractRelationship(normalized, 'activity')) || normalized.activity || {};
      const activityName =
        activityRelation?.title ||
        activityRelation?.name ||
        normalized?.activity?.title ||
        normalized?.activity?.name ||
        '';

      const mediaItems = extractMediaItems(normalized);
      const featuredMedia = mediaItems.find((media: any) => {
        const collectionName = media?.collection_name || media?.collectionName || '';
        return collectionName === 'cms_review_featured';
      });

      const content = translation?.content || normalized.content || '';
      const contentPreview = stripHtml(content);

      return {
        id: normalized.id,
        title: translation?.title || normalized.title || '-',
        content,
        contentPreview,
        rating: normalized.rating || 0,
        authorName: normalized.author_name || normalized.authorName || '',
        status: normalized.status || 'pending',
        reviewDate: normalized.review_date || normalized.reviewDate || null,
        reviewDateLabel: formatDate(normalized.review_date || normalized.reviewDate),
        activityId: normalized.cms_activity_id || normalized.activityId || null,
        activityName,
        updatedAt: normalized.updated_at || null,
        updatedAtLabel: formatDateTime(normalized.updated_at),
        locale: (translation?.locale || selectedLanguage.value || 'en').toLowerCase(),
        featuredImage: getMediaUrl(featuredMedia),
      };
    };

    const buildFilters = () => {
      const filters: Record<string, any> = {
        language: selectedLanguage.value || currentLocale.value,
      };

      if (selectedActivity.value) {
        const activityId = Number(selectedActivity.value);
        if (!Number.isNaN(activityId)) {
          filters.activity_id = activityId;
        }
      }

      if (selectedRating.value) {
        const rating = Number(selectedRating.value);
        if (!Number.isNaN(rating)) {
          filters.rating = rating;
        }
      }

      if (selectedStatus.value) {
        filters.status = selectedStatus.value;
      }

      if (searchQuery.value.trim()) {
        filters.search = searchQuery.value.trim();
      }

      return filters;
    };

    const loadReviews = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.reviewService.listCmsReviews(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters: buildFilters(),
          order: { key: 'review_date', direction: 'desc' },
        });
        const list = extractListData(response);
        reviews.value = list.map(mapReviewRecord);

        // Extract pagination meta from response
        if (response?.data?.meta || response?.meta) {
          const meta = response?.data?.meta || response?.meta;
          paginationMeta.value = {
            currentPage: meta.current_page || currentPage.value,
            lastPage: meta.last_page || 1,
            perPage: meta.per_page || perPage.value,
            from: meta.from || null,
            to: meta.to || null,
            total: meta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load CMS reviews', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const loadActivities = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          return {
            value: `${normalized.id}`,
            label: normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load activities', error);
      }
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadReviews();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1; // Reset to first page when changing per page
      loadReviews();
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadActivities();
          loadReviews();
        }
      },
      { immediate: true },
    );

    watch(
      [selectedLanguage, selectedActivity, selectedRating, selectedStatus],
      () => {
        if (!agencySlug.value) return;
        currentPage.value = 1;
        loadReviews();
      },
    );

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          loadReviews();
        }, 400);
      },
    );

    const navigateToCreate = () => {
      router.push({ name: 'cms.reviews.create' });
    };

    const navigateToEdit = (reviewId: number) => {
      router.push({ name: 'cms.reviews.edit', params: { id: reviewId } });
    };

    const updateStatus = async (review: CmsReviewCard, status: 'approved' | 'rejected') => {
      if (!agencySlug.value) return;
      try {
        await api.reviewService.updateCmsReviewStatus(agencySlug.value, review.id, status);
        review.status = status;
        toast.success(t('cms.reviews.statusUpdateSuccess'));
      } catch (error: any) {
        console.error('Failed to update review status', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      }
    };

    const deleteReview = async (review: CmsReviewCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = review.id;
      try {
        await api.reviewService.deleteCmsReview(agencySlug.value, review.id);
        reviews.value = reviews.value.filter((item) => item.id !== review.id);
        toast.success(t('cms.reviews.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete review', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    return {
      reviews,
      filteredReviews,
      selectedLanguage,
      selectedActivity,
      selectedRating,
      selectedStatus,
      searchQuery,
      languageOptions,
      activityOptions,
      ratingOptions,
      isLoading,
      errorMessage,
      deletingId,
      loadReviews,
      navigateToCreate,
      navigateToEdit,
      updateStatus,
      deleteReview,
      paginationMeta,
      handlePageChange,
      handlePerPageChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-reviews {
  color: $cms-text-primary;
}

// Loading state (unique to this view)
.cms-reviews-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

// Error state (unique to this view)
.cms-reviews-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

// Review card specific styles (unique to this view)
.cms-review-card {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-sm;
}

.cms-review-preview {
  width: 100%;
  height: 160px;
  border-radius: $cms-radius-md;
  background-size: cover;
  background-position: center;
  margin-bottom: $cms-spacing-sm;
}

.cms-review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $cms-spacing-sm;
}

.cms-review-title {
  font-size: $cms-font-size-lg;
  font-weight: 600;
  margin: 0;
  color: $cms-text-primary;
  flex: 1;
}

.cms-review-badges {
  display: flex;
  align-items: center;
  gap: $cms-spacing-xs;
}

.cms-status-badge {
  padding: $cms-spacing-xxs $cms-spacing-xs;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  text-transform: uppercase;

  &.status-pending {
    background: $cms-warning-light;
    color: $cms-warning-color;
  }

  &.status-approved {
    background: $cms-success-light;
    color: $cms-success-color;
  }

  &.status-rejected {
    background: $cms-danger-light;
    color: $cms-danger-color;
  }
}

.cms-review-rating {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: $cms-spacing-xxs $cms-spacing-xs;
  background: $cms-warning-light;
  color: $cms-warning-color;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-sm;
  font-weight: 600;
}

.cms-review-content {
  color: $cms-text-secondary;
  margin: $cms-spacing-sm 0;
  line-height: 1.45;
  font-size: $cms-font-size-sm;
  
  :deep(p) {
    margin: 0 0 $cms-spacing-xs 0;
  }
  
  :deep(p:last-child) {
    margin-bottom: 0;
  }
}

.cms-review-meta {
  list-style: none;
  padding: 0;
  margin: $cms-spacing-sm 0;
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-sm;

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: $cms-font-size-sm;
    color: $cms-text-secondary;
  }
}

.cms-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-review-date {
  color: $cms-text-secondary;
}

.cms-review-locale {
  font-weight: 600;
  color: $cms-primary-color;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 
